import { WorkspaceInvitations } from 'shared-ui';
import { environment } from '../../../environments/environment';
import { ownerRouteAllowed } from 'core-access';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { InactivityTimeoutService } from '../../core/services/session/inactivity-timeout.service';
import {
  OrganizationContextItem,
  OwnerActivityResponse,
  OwnerNotificationResponse,
  OwnerPortalService,
  OwnerEntityContext,
} from '../../core/services/owner-portal.service';
import { OwnerWorkspaceService } from '../../core/services/owner-workspace.service';
import { OwnerDateFormatService } from '../../core/services/owner-date-format.service';
import {
  normalizeOwnerActionUrl,
  ownerNotificationActionLabel,
  ownerNotificationFallbackTarget,
  resolveOwnerNotificationTarget,
} from '../../shared/owner-notification-routing';

type OwnerNavItem = {
  label: string;
  icon: string;
  route: string;
};

@Component({
  selector: 'app-owner-shell',
  standalone: true,
  imports: [WorkspaceInvitations, CommonModule, FormsModule, RouterLink, RouterLinkActive, RouterOutlet, SelectModule],
  templateUrl: './owner-shell.html',
  styleUrl: './owner-shell.scss',
})
export class OwnerShell implements OnInit, OnDestroy {
  protected readonly invitationEnvironment = environment;
  protected readonly dates = inject(OwnerDateFormatService);
  protected readonly pendingInvitations = signal(0);
  protected invitationDashboard(): boolean { return this.router.url.split('?')[0] === '/dashboard'; }
  private readonly workspace = inject(OwnerWorkspaceService);
  private readonly api = inject(OwnerPortalService);
  private readonly inactivityTimeout = inject(InactivityTimeoutService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly menuOpen = signal(false);
  protected readonly workspaceMenuOpen = signal(false);
  protected readonly workspaceOptions = signal<OrganizationContextItem[]>([]);
  protected readonly activeWorkspaceId = signal<string | null>(null);
  protected readonly theme = signal<'light' | 'dark'>('light');
  protected readonly notificationsOpen = signal(false);
  protected readonly notificationsLoading = signal(false);
  protected readonly notifications = signal<OwnerNotificationResponse[]>([]);
  protected readonly unreadNotificationCount = signal(0);
  protected readonly themeIcon = computed(() => this.theme() === 'dark' ? 'light_mode' : 'dark_mode');
  protected readonly themeLabel = computed(() => this.theme() === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
  protected readonly canSwitchWorkspaces = computed(() => this.workspaceOptions().length > 1);
  protected readonly ownerEntities = signal<OwnerEntityContext[]>([]);
  protected readonly ownerEntityId = signal<string | null>(null);
  protected readonly ownerEntityRequired = signal(false);
  protected readonly contextReady = signal(false);
  protected readonly entitySwitching = signal(false);
  protected readonly entityError = signal<string | null>(null);
  protected readonly contextFailed = signal(false);
  protected readonly workspaceMissing = signal(false);
  /**
   * What the main area shows. Every state other than "ready" explains itself, so the portal never
   * renders as an empty page (for example when an owner with several entities hasn't picked one yet).
   */
  protected readonly mainState = computed<'invited' | 'loading' | 'error' | 'no-workspace' | 'no-entities' | 'choose-entity' | 'switching' | 'ready'>(() => {
    if (this.contextFailed()) return 'error';
    if (!this.contextReady()) return 'loading';
    if (this.entitySwitching()) return 'switching';
    if (this.workspaceMissing()) return this.pendingInvitations() ? 'invited' : 'no-workspace';
    if (this.ownerEntityRequired() && !this.ownerEntities().length) return 'no-entities';
    if (this.ownerEntityRequired() && !this.ownerEntityId()) return 'choose-entity';
    return 'ready';
  });
  private notificationOrgId: string | null = null;
  private notificationEventSource: EventSource | null = null;

  private readonly allNavItems: OwnerNavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    { label: 'Properties', icon: 'business', route: '/properties' },
    { label: 'Financials', icon: 'account_balance_wallet', route: '/financials' },
    { label: 'Statements', icon: 'article', route: '/statements' },
    { label: 'Tax documents', icon: 'receipt_long', route: '/tax-documents' },
    { label: 'Maintenance', icon: 'construction', route: '/maintenance' },
    { label: 'Inspections', icon: 'fact_check', route: '/inspections' },
    { label: 'Documents', icon: 'folder', route: '/documents' },
    { label: 'Approvals', icon: 'approval', route: '/approvals' },
    { label: 'Updates', icon: 'notifications', route: '/messages' },
    { label: 'Messages', icon: 'forum', route: '/communications' },
    { label: 'Reports', icon: 'monitoring', route: '/reports' },
    { label: 'Audit trail', icon: 'history', route: '/audit-trail' },
  ];

  protected get navItems(): OwnerNavItem[] { return this.allNavItems.filter(item => ownerRouteAllowed(this.workspace.currentWorkspace(), item.route)); }

  protected readonly orgName = computed(() => {
    return this.workspace.currentBranding()?.orgName
      || this.workspace.currentWorkspace()?.orgName
      || 'Owner Portal';
  });

  protected readonly initials = computed(() => {
    return this.orgName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'OP';
  });

  ngOnInit(): void {
    // Private context depends on the browser session and is not fetched during SSR.
    if (!isPlatformBrowser(this.platformId)) return;
    this.inactivityTimeout.start();
    this.syncThemeFromDom();
    void this.loadWorkspaceOptions();
  }

  ngOnDestroy(): void {
    this.inactivityTimeout.stop();
    this.closeNotificationStream();
  }

  protected toggleTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const root = this.document.documentElement;
    const next = root.dataset['theme'] === 'dark' ? 'light' : 'dark';
    this.applyTheme(next);
    try {
      localStorage.setItem('apexOwnerColorScheme', next);
    } catch {
      // Theme persistence is optional.
    }
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected toggleWorkspaceMenu(): void {
    if (!this.canSwitchWorkspaces()) {
      this.switchWorkspace();
      return;
    }
    this.workspaceMenuOpen.update((open) => !open);
    this.notificationsOpen.set(false);
  }

  protected closeWorkspaceMenu(): void {
    this.workspaceMenuOpen.set(false);
  }

  protected toggleNotifications(): void {
    this.notificationsOpen.update((open) => !open);
    if (this.notificationsOpen()) {
      this.refreshNotifications();
    }
  }

  protected closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  protected markAllNotificationsRead(): void {
    if (!this.notificationOrgId) return;
    this.api.markAllNotificationsRead(this.notificationOrgId).subscribe({
      next: () => {
        this.unreadNotificationCount.set(0);
        this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
      },
    });
  }

  protected openNotification(notification: OwnerNotificationResponse): void {
    const orgId = this.notificationOrgId;
    if (orgId && !notification.read) {
      this.api.markNotificationRead(orgId, notification.id).subscribe({
        next: (updated) => {
          this.notifications.update((items) =>
            items.map((item) => (item.id === notification.id ? { ...item, ...updated, read: true } : item)),
          );
          this.unreadNotificationCount.update((count) => Math.max(0, count - 1));
        },
      });
    }

    this.notificationsOpen.set(false);
    this.openOwnerUrl(this.ownerNotificationTarget(notification));
  }

  protected openNotificationsPage(): void {
    this.notificationsOpen.set(false);
    void this.router.navigate(['/messages']);
  }

  protected notificationCountLabel(): string {
    const count = this.unreadNotificationCount();
    return count > 9 ? '9+' : String(count);
  }

  protected notificationTimeLabel(notification: OwnerNotificationResponse): string {
    const timestamp = Date.parse(notification.createdAt);
    if (Number.isNaN(timestamp)) return '';
    const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  protected notificationMessage(notification: OwnerNotificationResponse): string {
    return this.readableStatusText(notification.message || 'Review this update.');
  }

  protected notificationIcon(notification: OwnerNotificationResponse): string {
    const value = `${notification.type ?? ''} ${notification.referenceType ?? ''}`.toUpperCase();
    if (value.includes('MESSAGE')) return 'forum';
    if (value.includes('ANNOUNCEMENT')) return 'campaign';
    if (value.includes('STATEMENT')) return 'article';
    if (value.includes('DOCUMENT')) return 'folder';
    if (value.includes('APPROVAL')) return 'approval';
    if (value.includes('PAYMENT') || value.includes('PAYOUT')) return 'payments';
    if (value.includes('MAINTENANCE')) return 'construction';
    if (value.includes('INSPECTION') || value.includes('SELF_CHECK')) return 'fact_check';
    if (value.includes('REPORT')) return 'monitoring';
    if (value.includes('PROPERTY')) return 'business';
    return 'notifications';
  }

  protected notificationActionLabel(notification: OwnerNotificationResponse): string {
    const target = this.ownerNotificationTarget(notification);
    const value = `${notification.type ?? ''} ${notification.referenceType ?? ''}`.toUpperCase();
    if (target.includes('owner-payout-settings') || value.includes('OWNER_PAYOUT_REMINDER')) return 'Open payout setup';
    if (target.startsWith('/financials') && value.includes('PAYOUT')) return 'View payout history';
    return ownerNotificationActionLabel(target);
  }

  protected signOut(): void {
    void this.workspace.signOut();
  }

  protected reloadPortal(): void {
    if (isPlatformBrowser(this.platformId)) window.location.reload();
  }

  protected switchWorkspace(): void {
    this.workspace.switchWorkspace();
  }

  /** Tells same-named entities apart in the switcher: the company behind them and how much they own. */
  protected entityDetail(entity: OwnerEntityContext): string {
    const count = `${entity.propertyIds.length} ${entity.propertyIds.length === 1 ? 'property' : 'properties'}`;
    const company = entity.companyName?.trim();
    return company && company !== entity.displayName ? `${company} · ${count}` : count;
  }

  protected async chooseOwnerEntity(id: string): Promise<void> {
    if (!id || id === this.ownerEntityId()) return;
    this.entitySwitching.set(true);
    this.entityError.set(null);
    this.notifications.set([]);
    this.closeNotificationStream();
    try {
      await this.workspace.selectOwnerEntity(id);
    } catch {
      this.entityError.set('Unable to open this owner entity. Refresh and try again.');
      this.entitySwitching.set(false);
    }
  }

  protected switchToWorkspace(organization: OrganizationContextItem): void {
    if (organization.orgId === this.activeWorkspaceId()) {
      this.workspaceMenuOpen.set(false);
      return;
    }

    this.workspaceMenuOpen.set(false);
    this.workspace.switchToWorkspace(organization);
  }

  protected workspaceInitials(name: string | null | undefined): string {
    return (name || 'Workspace')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'W';
  }

  protected workspaceRoleLabel(organization: OrganizationContextItem): string {
    const portalMode = String(organization.portalMode ?? '').toUpperCase();
    const role = String(organization.orgRole ?? '').trim().replace(/_/g, ' ').toLowerCase();
    const label = role ? role.replace(/\b\w/g, (match) => match.toUpperCase()) : 'Workspace';
    if (portalMode === 'OWNER_PORTAL') return 'Owner Portal';
    if (portalMode === 'TENANT') return 'Tenant Portal';
    return label;
  }

  @HostListener('document:pointerdown', ['$event'])
  protected closeNotificationsOnOutsideClick(event: PointerEvent): void {
    if (!this.notificationsOpen() && !this.workspaceMenuOpen()) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    const menu = this.elementRef.nativeElement.querySelector('[data-owner-notifications-menu]');
    if (this.notificationsOpen() && !menu?.contains(target)) {
      this.notificationsOpen.set(false);
    }
    const workspaceMenu = this.elementRef.nativeElement.querySelector('[data-owner-workspace-menu]');
    if (this.workspaceMenuOpen() && !workspaceMenu?.contains(target)) {
      this.workspaceMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeNotificationsOnEscape(): void {
    this.notificationsOpen.set(false);
    this.workspaceMenuOpen.set(false);
  }

  private async loadWorkspaceOptions(): Promise<void> {
    try {
      const [workspace, options] = await Promise.all([
        this.workspace.resolveWorkspace(),
        this.workspace.workspaceOptions(),
      ]);
      this.activeWorkspaceId.set(workspace?.orgId ?? null);
      this.workspaceMissing.set(!workspace);
      this.workspaceOptions.set(options);
      this.ownerEntities.set(this.workspace.ownerEntities());
      this.ownerEntityId.set(this.workspace.selectedOwnerEntityId());
      this.ownerEntityRequired.set(this.workspace.requiresOwnerEntity());
      this.contextReady.set(true);
      if (workspace?.orgId) this.loadNotifications(workspace.orgId);
    } catch (error) {
      console.warn('[OwnerShell] owner access could not be loaded:', error);
      this.workspaceOptions.set([]);
      this.contextFailed.set(true);
    }
  }

  private loadNotifications(orgId: string): void {
    try {
      this.configureNotifications(orgId);
    } catch {
      // A notification transport failure must not hide an authorized workspace.
      this.notifications.set([]);
    }
  }

  private configureNotifications(orgId: string): void {
    if (this.notificationOrgId === orgId) return;
    this.notificationOrgId = orgId;
    this.refreshNotifications();
    this.openNotificationStream(orgId);
  }

  private refreshNotifications(): void {
    const orgId = this.notificationOrgId;
    if (!orgId) return;
    this.notificationsLoading.set(true);
    this.api.listNotifications(orgId).subscribe({
      next: (page) => {
        this.notifications.set(page.content ?? []);
        this.mergeOwnerActivityNotifications(orgId);
        this.notificationsLoading.set(false);
      },
      error: () => {
        this.notifications.set([]);
        this.mergeOwnerActivityNotifications(orgId);
        this.notificationsLoading.set(false);
      },
    });
    this.api.unreadNotificationCount(orgId).subscribe({
      next: (response) => this.unreadNotificationCount.set(response.unreadCount ?? 0),
      error: () => this.unreadNotificationCount.set(0),
    });
  }

  private mergeOwnerActivityNotifications(orgId: string): void {
    if (this.workspace.requiresOwnerEntity() && !this.workspace.selectedOwnerEntityId()) return;
    this.api.getCurrentOwnerActivity(orgId, 0, 12).subscribe({
      next: (page) => {
        const activityNotifications = (page.content ?? [])
          .filter((activity) => this.shouldShowActivityInBell(activity))
          .map((activity) => this.activityNotification(activity));

        if (!activityNotifications.length) return;

        this.notifications.update((items) => {
          const existingIds = new Set(items.map((item) => item.id));
          return [
            ...items,
            ...activityNotifications.filter((item) => !existingIds.has(item.id)),
          ]
            .sort((a, b) => this.notificationTimestamp(b) - this.notificationTimestamp(a))
            .slice(0, 10);
        });
      },
      error: () => undefined,
    });
  }

  private shouldShowActivityInBell(activity: OwnerActivityResponse): boolean {
    const value = `${activity.action ?? ''} ${activity.referenceType ?? ''} ${activity.title ?? ''} ${activity.description ?? ''}`.toUpperCase();
    return [
      'ANNOUNCEMENT',
      'OWNER_MESSAGE',
      'OWNER_STATEMENT',
      'STATEMENT',
      'DOCUMENT',
      'REPORT',
      'APPROVAL',
      'MAINTENANCE',
      'INSPECTION',
      'PROPERTY',
      'PAYOUT',
      'PAYMENT',
    ].some((keyword) => value.includes(keyword));
  }

  private activityNotification(activity: OwnerActivityResponse): OwnerNotificationResponse {
    const value = `${activity.action ?? ''} ${activity.referenceType ?? ''}`.toUpperCase();
    const title = activity.title?.trim() || this.humanizeActivityLabel(activity.action || activity.referenceType || 'Owner update');
    return {
      id: `activity:${activity.id}`,
      type: activity.action || activity.referenceType || 'OWNER_ACTIVITY',
      title,
      message: activity.description?.trim() || title,
      actionUrl: ownerNotificationFallbackTarget({
        type: activity.action,
        referenceType: activity.referenceType,
        referenceId: activity.referenceId ?? activity.id,
      }),
      referenceType: activity.referenceType ?? null,
      referenceId: activity.referenceId ?? activity.id,
      read: true,
      createdAt: activity.createdDate || new Date(0).toISOString(),
    };
  }

  private notificationTimestamp(notification: OwnerNotificationResponse): number {
    const timestamp = Date.parse(notification.createdAt);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private openNotificationStream(orgId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.closeNotificationStream();
    this.notificationEventSource = new EventSource(this.api.notificationStreamUrl(orgId), {
      withCredentials: true,
    });

    this.notificationEventSource.addEventListener('connected', (event) => {
      this.applyUnreadCountEvent(event);
    });
    this.notificationEventSource.addEventListener('unread-count', (event) => {
      this.applyUnreadCountEvent(event);
    });
    this.notificationEventSource.addEventListener('notification', (event) => {
      const notification = this.parseEventData<OwnerNotificationResponse>(event);
      if (!notification?.id) return;
      this.notifications.update((items) => [
        notification,
        ...items.filter((item) => item.id !== notification.id),
      ].slice(0, 10));
      if (!notification.read) {
        this.unreadNotificationCount.update((count) => count + 1);
      }
    });
    this.notificationEventSource.onerror = () => {
      this.closeNotificationStream();
    };
  }

  private closeNotificationStream(): void {
    this.notificationEventSource?.close();
    this.notificationEventSource = null;
  }

  private applyUnreadCountEvent(event: Event): void {
    const data = this.parseEventData<{ unreadCount?: number }>(event);
    if (typeof data?.unreadCount === 'number') {
      this.unreadNotificationCount.set(data.unreadCount);
    }
  }

  private parseEventData<T>(event: Event): T | null {
    try {
      const data = (event as MessageEvent).data;
      return data ? JSON.parse(data) as T : null;
    } catch {
      return null;
    }
  }

  private openOwnerUrl(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const normalizedUrl = normalizeOwnerActionUrl(url, window.location.origin) ?? '/messages';
    if (!normalizedUrl || /^javascript:/i.test(normalizedUrl)) {
      void this.router.navigateByUrl('/messages');
      return;
    }
    if (/^https?:\/\//i.test(normalizedUrl)) {
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    void this.router.navigateByUrl(normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`);
  }

  private ownerNotificationTarget(notification: OwnerNotificationResponse): string {
    return resolveOwnerNotificationTarget(
      notification,
      isPlatformBrowser(this.platformId) ? window.location.origin : null,
    );
  }

  private humanizeActivityLabel(value: string): string {
    const text = String(value || 'Owner update')
      .replace(/^OWNER_/, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (match) => match.toUpperCase());
    return text || 'Owner update';
  }

  private readableStatusText(value: string): string {
    return value
      .replace(/\bIN_PROGRESS\b/g, 'In progress')
      .replace(/\bCANCELLED\b/g, 'Cancelled')
      .replace(/\bSCHEDULED\b/g, 'Scheduled')
      .replace(/\bRESOLVED\b/g, 'Resolved')
      .replace(/\bCLOSED\b/g, 'Closed')
      .replace(/\bOPEN\b/g, 'Open');
  }

  private syncThemeFromDom(): void {
    if (!this.document.documentElement) return;
    const current = this.document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light';
    this.applyTheme(current);
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    const root = this.document.documentElement;
    if (!root) return;
    root.dataset['theme'] = theme;
    root.dataset['themePreference'] = theme;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
    if (this.document.body) {
      this.document.body.dataset['theme'] = theme;
      this.document.body.classList.toggle('dark', theme === 'dark');
    }
    this.theme.set(theme);
  }
}
